import { Box } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getSelectedCatalogItem } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { getDisableChangeSupplierAndDeleteItems } from 'entities/Order/model/selectors/ordersPermissions';
import {
    getSelectedOrder,
    getSelectedOrderId,
    getSelectedOrderIsPreSubmission,
    getSelectedOrderItems,
    getSelectedOrderStatus,
} from 'entities/Order/model/selectors/ordersSelectors';
import { getSelectedProperty } from 'entities/Property/model/selectors/PropertySelectors';
import { HydratedProposal } from 'entities/Proposal/model/types/ProposalSchema';
import { getIsReOpsUser } from 'entities/User';
import { getPropertyVendorsMap } from 'entities/Vendors/model/selectors/vendorsSelectors';
import {
    SaveCancelButtons,
    editOrderItemSourcingActions,
    getIsInitialized,
    getQuantityExceedsStockError,
    getSupplierActivationStatusError,
} from 'features/EditOrderItemSourcing';
import {
    getRouteOrdersView,
    getRouteSettingsOrdersSourcing,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useUnmountEffect } from 'shared/lib/hooks/useUnmountEffect';
import { BackButton } from 'shared/ui/BackButton';

import { OrderViewItemDetailedInfo } from './OrderViewItemDetailedInfo';
import { OrderViewItemSupplierTable } from './OrderViewItemSupplierTable';

import {
    SaveBarError,
    useEditOrderItemInit,
    useLoadingState,
    useSortedProposals,
    useUnselectedProposals,
} from '../lib';

export const OrderViewItemDetailed = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const selectedItem = useSelector(getSelectedCatalogItem);
    const vendorsMap = useSelector(getPropertyVendorsMap);
    const orderId = useSelector(getSelectedOrderId);
    const selectedOrder = useSelector(getSelectedOrder);
    const selectedItems = useSelector(getSelectedOrderItems);
    const isInitialized = useSelector(getIsInitialized);
    const disableChangeSupplierAndDeleteItems = useSelector(
        getDisableChangeSupplierAndDeleteItems,
    );
    const selectedProperty = useSelector(getSelectedProperty);
    const quantityExceedsStockError = useSelector(getQuantityExceedsStockError);
    const supplierActivationStatusError = useSelector(
        getSupplierActivationStatusError,
    );
    const isPreSubmission = useSelector(getSelectedOrderIsPreSubmission);
    const orderStatus = useSelector(getSelectedOrderStatus);
    const isReOpsUser = useSelector(getIsReOpsUser);

    const itemId =
        selectedItem && 'id' in selectedItem ? selectedItem.id : undefined;
    const selectedItemData = selectedItems?.find((item) => item.id === itemId);

    const memoizedSelectedItemData = useMemo(() => {
        return selectedItemData
            ? {
                  selectedProposals: selectedItemData.selectedProposals,
                  proposals: selectedItemData.proposals,
                  isSourcingOverridden:
                      selectedItemData.isSourcingOverridden || false,
              }
            : undefined;
    }, [selectedItemData]);

    // Check if we have all the data we need to display the table
    // Some of this is async and can be out of sync on a page refresh
    const loadingRequiredData = useLoadingState({
        selectedPropertyId: selectedProperty?.id,
        selectedOrderProperty: selectedOrder?.property,
        vendorsMap,
    });

    // Pull out hydrated and sorted proposals ready for the table to use
    const sortedProposals: HydratedProposal[] = useSortedProposals({
        proposals: selectedItemData?.proposals,
        vendorsMap,
        selectedProposals: selectedItemData?.selectedProposals,
        selectedOrderProperty: selectedOrder?.property,
        selectedPropertyId: selectedProperty?.id,
        sourcingSettings: selectedProperty?.sourcingSettings,
        isPreSubmission,
    });

    // For RE-OPS users viewing submitted orders, get unselected proposals from allCheckResults
    const unselectedProposals = useUnselectedProposals({
        isReOpsUser,
        orderStatus,
        allCheckResults: selectedItemData?.allCheckResults,
        selectedProposals: selectedItemData?.selectedProposals,
        proposals: selectedItemData?.proposals,
        vendorsMap,
    });

    // Initialize EditOrderItemSourcing when component mounts or data changes
    useEditOrderItemInit({
        selectedItemData: memoizedSelectedItemData,
        orderId,
        itemId,
        selectedQuantity: selectedItemData?.qty || 0,
        vendorsMap,
        isPreSubmission,
    });

    const onBack = useCallback(() => {
        if (orderId) {
            navigate(getRouteOrdersView(orderId));
        }
    }, [navigate, orderId]);

    useUnmountEffect(() => {
        dispatch(catalogItemActions.setSelectedItem(null));
        dispatch(editOrderItemSourcingActions.clearState());
    });

    return (
        <Box>
            <Box
                p="16px 16px 0px"
                height={'calc(100vh - 204px)'}
                sx={{ overflowY: 'scroll' }}
            >
                <Box display="flex" flexDirection="column" gap="8px">
                    <Box>
                        <BackButton
                            onBack={onBack}
                            title={t('Order details')}
                            marginBottom={'26px'}
                        />
                    </Box>
                    <Box mb={'16px'}>
                        <OrderViewItemDetailedInfo />
                    </Box>
                    <OrderViewItemSupplierTable
                        loadingRequiredData={loadingRequiredData}
                        selectedItem={selectedItem as CatalogItem}
                        selectedOrder={selectedOrder}
                        proposals={sortedProposals}
                        unselectedProposals={unselectedProposals}
                    />
                </Box>
            </Box>
            {isPreSubmission && (
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    p="12px 16px 16px"
                    bgcolor="background.paper"
                    borderTop="1px solid"
                    borderColor="divider"
                >
                    {!disableChangeSupplierAndDeleteItems && isInitialized && (
                        <>
                            {supplierActivationStatusError && (
                                <SaveBarError
                                    message={t(
                                        'must be enabled to submit order.',
                                    )}
                                    actionText={t('Supplier accounts')}
                                    onAction={() => {
                                        if (selectedProperty?.id) {
                                            navigate(
                                                getRouteSettingsOrdersSourcing(
                                                    selectedProperty.id,
                                                    'operations',
                                                ),
                                            );
                                        }
                                    }}
                                />
                            )}
                            {quantityExceedsStockError &&
                                !supplierActivationStatusError && (
                                    <SaveBarError
                                        message={t(
                                            'Requested quantity for this item exceeds available stock.',
                                        )}
                                    />
                                )}

                            <Box ml="auto">
                                <SaveCancelButtons onCancel={onBack} />
                            </Box>
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};
